import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { nguoiDungAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import {
  FiUsers,
  FiBook,
  FiUserCheck,
  FiClipboard,
  FiCalendar,
  FiArrowRight,
  FiCheckCircle,
  FiCircle,
  FiUpload,
  FiPlusCircle,
  FiSliders,
  FiGrid,
  FiDownload,
  FiMove,
  FiShield,
  FiZap,
  FiEye,
  FiEdit,
  FiSun,
  FiClock,
  FiAlertCircle
} from 'react-icons/fi';

const Dashboard = () => {
  const { t } = useTranslation();
  const { user, setAdminViewUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    tongLopHoc: 0,
    tongMonHoc: 0,
    tongGiaoVien: 0,
    tongPhanCong: 0,
    tongTietHoc: 0
  });
  const [loading, setLoading] = useState(true);
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  useEffect(() => {
    fetchDashboard();
    if (user?.isAdmin) fetchAllUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.isAdmin]);

  const fetchDashboard = async () => {
    try {
      const response = await nguoiDungAPI.getDashboard();
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      toast.error(t('dashboard.cannotLoadDashboard'));
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await nguoiDungAPI.getAllUsers();
      if (response.data.success) setAllUsers(response.data.data);
    } catch {
      toast.error('Không thể tải danh sách người dùng.');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleViewUserData = (targetUser, path) => {
    setAdminViewUser({ userId: targetUser.userId, hoTen: targetUser.hoTen, email: targetUser.email });
    navigate(path);
  };

  const statCards = [
    { label: t('dashboard.statClasses'),     value: stats.tongLopHoc,   icon: FiUsers,     color: 'blue',   link: '/lop-hoc'  },
    { label: t('dashboard.statSubjects'),    value: stats.tongMonHoc,   icon: FiBook,      color: 'green',  link: '/mon-hoc'  },
    { label: t('dashboard.statTeachers'),    value: stats.tongGiaoVien, icon: FiUserCheck, color: 'purple', link: '/giao-vien'},
    { label: t('dashboard.statAssignments'), value: stats.tongPhanCong, icon: FiClipboard, color: 'orange', link: '/phan-cong'},
    { label: t('dashboard.statPeriods'),     value: stats.tongTietHoc,  icon: FiCalendar,  color: 'red',    link: '/tkb'      },
  ];

  const thStyle = { padding: '10px 14px', fontWeight: 600, fontSize: '13px', color: 'var(--text-secondary)', textAlign: 'left', whiteSpace: 'nowrap' };
  const tdStyle = { padding: '10px 14px', color: 'var(--text-primary)' };
  const statBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontWeight: 700, fontSize: '15px', padding: '2px 6px', borderRadius: '6px', transition: 'background 0.15s' };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div>
      {/* ===== Stat Cards ===== */}
      <div className="dashboard-stats">
        {statCards.map((card, index) => (
          <Link to={card.link} key={index} className="stat-card">
            <div className={`stat-icon ${card.color}`}>
              <card.icon />
            </div>
            <div className="stat-info">
              <h3>{card.value}</h3>
              <p>{card.label}</p>
            </div>
          </Link>
        ))}
      </div>

      {/* ===== Hướng dẫn sử dụng ===== */}
      <div className="card">
        <div className="card-header" style={{ paddingBottom: '12px' }}>
          <h2> {t('dashboard.guideTitle')}</h2>
          <p style={{ margin: '4px 0 0', fontSize: '14px', color: 'var(--text-secondary)' }}>
            {t('dashboard.guideSubtitle')}
          </p>
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* Bước 1: Giáo viên */}
            <Link to="/danh-muc" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="guide-step">
                <div className="guide-step-left">
                  <div className="guide-badge purple">
                    {stats.tongGiaoVien > 0 ? <FiCheckCircle size={22} /> : <span>1</span>}
                  </div>
                  <div className="guide-connector"></div>
                </div>
                <div className="guide-step-body">
                  <div className="guide-step-header">
                    <div>
                      <div className="guide-step-title">
                        <FiUserCheck size={16} />
                        Nhập danh sách Giáo viên
                      </div>
                      <div className="guide-step-subtitle">Tab <strong>Giáo viên</strong> trong trang Lớp, Môn học &amp; GV</div>
                    </div>
                    <div className="guide-step-meta">
                      {stats.tongGiaoVien > 0
                        ? <span className="badge-done">{stats.tongGiaoVien} giáo viên</span>
                        : <span className="badge-pending">Chưa nhập</span>}
                      <FiArrowRight className="arrow-icon" />
                    </div>
                  </div>
                  <ul className="guide-tips">
                    <li><FiPlusCircle /> Nhấn <strong>Thêm giáo viên</strong>, nhập họ tên, chuyên môn, email và số điện thoại.</li>
                    <li><FiUpload /> Dùng <strong>Import Excel</strong> để thêm hàng loạt giáo viên từ danh sách nhân sự theo mẫu có sẵn.</li>
                    <li><FiSliders /> Sắp xếp thứ tự giáo viên bằng nút ▲▼ để hiển thị đúng thứ tự mong muốn trên TKB giáo viên.</li>
                    <li><FiCircle /> Thông tin <strong>chuyên môn</strong> giúp xác định môn học mà giáo viên có thể được phân công dạy.</li>
                  </ul>
                </div>
              </div>
            </Link>

            {/* Bước 2: Khối học */}
            <Link to="/danh-muc" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="guide-step">
                <div className="guide-step-left">
                  <div className="guide-badge indigo">
                    {stats.tongLopHoc > 0 ? <FiCheckCircle size={22} /> : <span>2</span>}
                  </div>
                  <div className="guide-connector"></div>
                </div>
                <div className="guide-step-body">
                  <div className="guide-step-header">
                    <div>
                      <div className="guide-step-title">
                        <FiGrid size={16} />
                        Tạo Khối học
                      </div>
                      <div className="guide-step-subtitle">Tab <strong>Khối học</strong> — nhóm các lớp cùng cấp/khối</div>
                    </div>
                    <div className="guide-step-meta">
                      <span className="badge-info">Tùy chọn</span>
                      <FiArrowRight className="arrow-icon" />
                    </div>
                  </div>
                  <ul className="guide-tips">
                    <li><FiPlusCircle /> Tạo các khối như <strong>Khối 10, Khối 11, Khối 12</strong> (hoặc Khối 1, 2... với tiểu học).</li>
                    <li><FiCircle /> Mỗi khối có <strong>mã khối</strong> (ví dụ: K10) dùng để nhận biết nhanh trong bảng TKB.</li>
                    <li><FiSliders /> Sắp xếp thứ tự khối — ảnh hưởng đến thứ tự cột lớp hiển thị trên thời khóa biểu.</li>
                    <li><FiBook /> Các lớp học, môn học và phân công đều gắn với khối để lọc và xếp TKB chính xác hơn.</li>
                  </ul>
                </div>
              </div>
            </Link>

            {/* Bước 3: Lớp học */}
            <Link to="/danh-muc" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="guide-step">
                <div className="guide-step-left">
                  <div className="guide-badge blue">
                    {stats.tongLopHoc > 0 ? <FiCheckCircle size={22} /> : <span>3</span>}
                  </div>
                  <div className="guide-connector"></div>
                </div>
                <div className="guide-step-body">
                  <div className="guide-step-header">
                    <div>
                      <div className="guide-step-title">
                        <FiUsers size={16} />
                        Nhập danh sách Lớp học
                      </div>
                      <div className="guide-step-subtitle">Tab <strong>Lớp học</strong> — gán khối, buổi học, GVCN và phòng</div>
                    </div>
                    <div className="guide-step-meta">
                      {stats.tongLopHoc > 0
                        ? <span className="badge-done">{stats.tongLopHoc} lớp học</span>
                        : <span className="badge-pending">Chưa nhập</span>}
                      <FiArrowRight className="arrow-icon" />
                    </div>
                  </div>
                  <ul className="guide-tips">
                    <li><FiPlusCircle /> Nhấn <strong>Thêm lớp</strong>, nhập tên lớp, chọn khối, buổi học (Sáng/Chiều), giáo viên chủ nhiệm và phòng học.</li>
                    <li><FiSun /> Cột <strong>Buổi học</strong>: chọn ☀️ Sáng hoặc 🌙 Chiều cho từng lớp — thuật toán sẽ ưu tiên xếp đúng buổi đó.</li>
                    <li><FiZap /> Nhấn <strong>Đặt buổi ngẫu nhiên</strong> để tự động phân chia Sáng/Chiều cân bằng trong từng khối.</li>
                    <li><FiUpload /> Dùng <strong>Import Excel</strong> để thêm hàng loạt lớp theo mẫu; sắp xếp thứ tự lớp bằng nút ▲▼.</li>
                  </ul>
                </div>
              </div>
            </Link>

            {/* Bước 4: Môn học */}
            <Link to="/danh-muc" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="guide-step">
                <div className="guide-step-left">
                  <div className="guide-badge green">
                    {stats.tongMonHoc > 0 ? <FiCheckCircle size={22} /> : <span>4</span>}
                  </div>
                  <div className="guide-connector"></div>
                </div>
                <div className="guide-step-body">
                  <div className="guide-step-header">
                    <div>
                      <div className="guide-step-title">
                        <FiBook size={16} />
                        Nhập danh sách Môn học
                      </div>
                      <div className="guide-step-subtitle">Tab <strong>Môn học</strong> — số tiết/tuần và giới hạn tiết/buổi</div>
                    </div>
                    <div className="guide-step-meta">
                      {stats.tongMonHoc > 0
                        ? <span className="badge-done">{stats.tongMonHoc} môn học</span>
                        : <span className="badge-pending">Chưa nhập</span>}
                      <FiArrowRight className="arrow-icon" />
                    </div>
                  </div>
                  <ul className="guide-tips">
                    <li><FiPlusCircle /> Nhấn <strong>Thêm môn học</strong>, nhập tên môn, chọn khối và nhập <strong>số tiết/tuần</strong> cho môn đó.</li>
                    <li><FiSliders /> Thiết lập <strong>số tiết tối đa trong 1 buổi</strong> để tránh xếp quá nhiều tiết liên tiếp (ví dụ: Toán tối đa 2 tiết/buổi).</li>
                    <li><FiUpload /> Dùng <strong>Import Excel</strong> để nhập hàng loạt: Tên môn, Khối, Số tiết/tuần, Số tiết tối đa/buổi.</li>
                    <li><FiCircle /> Mỗi môn học có thể được dạy ở nhiều khối khác nhau với số tiết khác nhau.</li>
                  </ul>
                </div>
              </div>
            </Link>

            {/* Bước 5: Phân công */}
            <Link to="/phan-cong" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="guide-step">
                <div className="guide-step-left">
                  <div className="guide-badge orange">
                    {stats.tongPhanCong > 0 ? <FiCheckCircle size={22} /> : <span>5</span>}
                  </div>
                  <div className="guide-connector"></div>
                </div>
                <div className="guide-step-body">
                  <div className="guide-step-header">
                    <div>
                      <div className="guide-step-title">
                        <FiClipboard size={16} />
                        Phân công giảng dạy
                      </div>
                      <div className="guide-step-subtitle">Gán giáo viên vào từng môn học của từng lớp</div>
                    </div>
                    <div className="guide-step-meta">
                      {stats.tongPhanCong > 0
                        ? <span className="badge-done">{stats.tongPhanCong} phân công</span>
                        : <span className="badge-pending">Chưa phân công</span>}
                      <FiArrowRight className="arrow-icon" />
                    </div>
                  </div>
                  <ul className="guide-tips">
                    <li><FiUserCheck /> Chọn một <strong>giáo viên</strong> từ danh sách bên trái để xem bảng phân công của GV đó.</li>
                    <li><FiGrid /> Bảng hiển thị tất cả <strong>môn học × lớp học</strong>. Tích vào ô để phân công, bỏ tích để hủy.</li>
                    <li><FiCheckCircle /> Ô <span style={{ background: '#e8f5e9', border: '1px solid #4caf50', borderRadius: '3px', padding: '0 4px', fontSize: '12px' }}>xanh</span> = GV này đang dạy; <span style={{ background: '#fff3e0', border: '1px solid #ffcc80', borderRadius: '3px', padding: '0 4px', fontSize: '12px' }}>vàng</span> = đã có GV khác dạy.</li>
                    <li><FiCircle /> Mỗi lớp–môn chỉ nên phân công cho <strong>1 giáo viên</strong> để thuật toán xếp TKB chính xác.</li>
                  </ul>
                </div>
              </div>
            </Link>

            {/* Bước 6: Thời khóa biểu */}
            <Link to="/tkb" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="guide-step guide-step-final">
                <div className="guide-step-left" style={{ paddingBottom: '20px' }}>
                  <div className="guide-badge primary">
                    {stats.tongTietHoc > 0 ? <FiCheckCircle size={22} /> : <span>6</span>}
                  </div>
                </div>
                <div className="guide-step-body">
                  <div className="guide-step-header">
                    <div>
                      <div className="guide-step-title" style={{ fontSize: '17px' }}>
                        <FiCalendar size={17} />
                        Xếp Thời khóa biểu
                      </div>
                      <div className="guide-step-subtitle">Tự động và thủ công — xuất Excel từ trang Kết quả TKB</div>
                    </div>
                    <div className="guide-step-meta">
                      {stats.tongTietHoc > 0
                        ? <span className="badge-done">{stats.tongTietHoc} tiết đã xếp</span>
                        : <span className="badge-pending">Chưa xếp</span>}
                      <FiArrowRight className="arrow-icon" />
                    </div>
                  </div>
                  <ul className="guide-tips">
                    <li><FiEye /> <strong>Hiển thị</strong>: chọn chế độ xem — <strong>Màu sắc</strong> (phân biệt môn), <strong>Nổi bật</strong> (tô đậm tiết chọn), <strong>Vùng học</strong> (xem vùng xếp).</li>
                    <li><FiEdit /> <strong>Bôi đánh dấu</strong>: dropdown chọn chế độ — <em>Vùng xếp</em> (tô khung buổi học), <em>Bôi môn</em> (gán màu môn), <em>Cố định môn / Bỏ cố định</em> (khóa/mở tiết).</li>
                    <li><FiGrid /> <strong>Tạo vùng xếp TKB tự động</strong>: thuật toán tự tính các buổi và ngày học tối ưu cho từng lớp dựa theo buổi học đã thiết lập — kết quả hiện ở chế độ <em>Vùng học</em>.</li>
                    <li><FiZap /> <strong>Tạo thời khóa biểu tự động</strong>: điền toàn bộ tiết học vào lưới TKB theo phân công và ràng buộc — kết quả hiện ở chế độ <em>Màu sắc</em>.</li>
                    <li style={{ listStyle: 'none', padding: 0, margin: '4px 0' }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '6px', padding: '8px 10px' }}>
                        <FiAlertCircle style={{ color: '#d97706', flexShrink: 0, marginTop: '2px' }} />
                        <span style={{ lineHeight: '1.5' }}>
                          <strong style={{ color: '#92400e' }}>Chú ý — Sinh hoạt:</strong>{' '}
                          Khi nhấn <em>Tạo TKB tự động</em>, hệ thống tự động xếp môn <strong>Sinh hoạt</strong> vào <strong>Tiết 1, Thứ Hai, đầu buổi của lớp</strong> và cố định trước khi xếp các môn còn lại.
                          Hệ thống nhận diện khi tên môn chứa <strong>"Sinh hoạt"</strong> hoặc viết tắt <strong>"SH"</strong> (không phân biệt hoa/thường).
                        </span>
                      </div>
                    </li>
                    <li><FiClock /> <strong>Xếp TKB buổi đang chọn</strong>: click chọn một ô trên lưới để xác định buổi, sau đó nhấn nút để xếp riêng buổi đó.</li>
                    <li><FiMove /> <strong>Đổi chỗ thủ công</strong>: nhấn nút <em>Đổi chỗ: TẮT/BẬT</em> trên toolbar để bật chế độ kéo thả. Khi bật — click chọn tiết nguồn (ô sáng lên), sau đó click ô đích để hoán đổi vị trí hai tiết. Nhấn lại nút để tắt khi xong.</li>
                    <li><FiSliders /> <strong>Zoom − / +</strong> (góc phải toolbar) để phóng to hoặc thu nhỏ lưới — giá trị zoom được lưu tự động.</li>
                    <li><FiDownload /> Vào trang <strong>Kết quả TKB</strong> để xem và xuất file Excel (TKB theo lớp + theo giáo viên).</li>
                  </ul>
                </div>
              </div>
            </Link>

          </div>
        </div>
      </div>

      {/* ===== Admin: Bảng người dùng ===== */}
      {user?.isAdmin && (
        <div className="card" style={{ marginTop: '24px' }}>
          <div className="card-header" style={{ paddingBottom: '12px' }}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FiShield style={{ color: '#6366f1' }} />
              Quản trị viên — Danh sách người dùng
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: '14px', color: 'var(--text-secondary)' }}>
              Click vào số liệu để xem dữ liệu tương ứng của người đó.
            </p>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {usersLoading ? (
              <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>Đang tải...</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid var(--border-color)' }}>
                      <th style={thStyle}>Họ tên</th>
                      <th style={thStyle}>Email</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Lớp</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Môn</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Giáo viên</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Phân công</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Tiết TKB</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Vai trò</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allUsers.map((u, i) => (
                      <tr key={u.userId} style={{ borderBottom: '1px solid var(--border-color)', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <td style={tdStyle}>{u.hoTen}</td>
                        <td style={tdStyle}>{u.email}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <button onClick={() => handleViewUserData(u, '/lop-hoc')} style={statBtnStyle}>{u.tongLopHoc}</button>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <button onClick={() => handleViewUserData(u, '/mon-hoc')} style={statBtnStyle}>{u.tongMonHoc}</button>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <button onClick={() => handleViewUserData(u, '/giao-vien')} style={statBtnStyle}>{u.tongGiaoVien}</button>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <button onClick={() => handleViewUserData(u, '/phan-cong')} style={statBtnStyle}>{u.tongPhanCong}</button>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <button onClick={() => handleViewUserData(u, '/tkb')} style={statBtnStyle}>{u.tongTietHoc}</button>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          {u.isAdmin
                            ? <span style={{ background: '#ede9fe', color: '#7c3aed', borderRadius: '12px', padding: '2px 8px', fontSize: '12px', fontWeight: 600 }}>Admin</span>
                            : <span style={{ background: '#f1f5f9', color: '#64748b', borderRadius: '12px', padding: '2px 8px', fontSize: '12px' }}>Người dùng</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        .guide-step {
          display: flex;
          gap: 0;
          background: #fff;
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius-lg);
          overflow: hidden;
          transition: var(--transition);
          cursor: pointer;
        }
        .guide-step:hover {
          box-shadow: var(--shadow-md);
          transform: translateY(-2px);
          border-color: #c7d2fe;
        }
        .guide-step-final {
          border: 2px solid var(--primary-color);
          background: linear-gradient(135deg, rgba(99,102,241,0.04) 0%, rgba(139,92,246,0.04) 100%);
        }
        .guide-step-final:hover {
          border-color: var(--primary-hover);
          box-shadow: var(--shadow-glow);
        }
        .guide-step-left {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 20px 0 0;
          width: 64px;
          flex-shrink: 0;
        }
        .guide-connector {
          flex: 1;
          width: 2px;
          background: var(--border-color);
          margin: 8px 0;
        }
        .guide-badge {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          font-size: 17px;
          flex-shrink: 0;
        }
        .guide-badge.blue    { background: linear-gradient(135deg, var(--info-color) 0%, var(--cyan-color) 100%); box-shadow: 0 4px 12px rgba(6,182,212,0.35); }
        .guide-badge.green   { background: linear-gradient(135deg, var(--success-color) 0%, var(--lime-color) 100%); box-shadow: 0 4px 12px rgba(16,185,129,0.35); }
        .guide-badge.purple  { background: linear-gradient(135deg, var(--purple-color) 0%, var(--pink-color) 100%); box-shadow: 0 4px 12px rgba(139,92,246,0.35); }
        .guide-badge.indigo  { background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%); box-shadow: 0 4px 12px rgba(99,102,241,0.35); }
        .guide-badge.orange  { background: linear-gradient(135deg, var(--orange-color) 0%, var(--danger-color) 100%); box-shadow: 0 4px 12px rgba(251,146,60,0.35); }
        .guide-badge.primary { background: var(--primary-gradient); box-shadow: var(--shadow-glow); width: 48px; height: 48px; font-size: 20px; }
        .badge-info {
          background: #eff6ff;
          color: #1d4ed8;
          border: 1px solid #bfdbfe;
          border-radius: 20px;
          padding: 2px 10px;
          font-size: 12px;
          font-weight: 600;
          white-space: nowrap;
        }
        .guide-step-body {
          flex: 1;
          padding: 18px 20px 18px 0;
          min-width: 0;
        }
        .guide-step-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 12px;
        }
        .guide-step-title {
          display: flex;
          align-items: center;
          gap: 7px;
          font-size: 15px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 3px;
        }
        .guide-step-subtitle {
          font-size: 13px;
          color: var(--text-secondary);
        }
        .guide-step-meta {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-shrink: 0;
        }
        .arrow-icon {
          color: var(--text-light);
          flex-shrink: 0;
        }
        .badge-done {
          background: #dcfce7;
          color: #16a34a;
          border: 1px solid #86efac;
          border-radius: 20px;
          padding: 2px 10px;
          font-size: 12px;
          font-weight: 600;
          white-space: nowrap;
        }
        .badge-pending {
          background: #fef3c7;
          color: #92400e;
          border: 1px solid #fcd34d;
          border-radius: 20px;
          padding: 2px 10px;
          font-size: 12px;
          font-weight: 600;
          white-space: nowrap;
        }
        .guide-tips {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .guide-tips li {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          font-size: 13.5px;
          color: var(--text-secondary);
          line-height: 1.5;
        }
        .guide-tips li svg {
          flex-shrink: 0;
          margin-top: 3px;
          color: var(--text-light);
        }
        .guide-tips li strong {
          color: var(--text-primary);
        }
        @media (max-width: 600px) {
          .guide-step-meta { display: none; }
          .guide-step-body { padding-right: 12px; }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
